/**
 * Embeddable Chat Widget - Loader
 *
 * Usage (paste into any website):
 *   <script src="https://your-cdn.com/widget-loader.js"
 *           data-client-id="YOUR_CLIENT_ID"
 *           async></script>
 *
 * The client-id ties this widget instance to a dashboard config.
 * All branding, prompt, voice settings, starter questions etc.
 * are fetched from the API using this client-id at runtime.
 */
(function () {
  function getLoaderScript(): HTMLScriptElement | null {
    const cur = document.currentScript as HTMLScriptElement | null;
    if (cur) return cur;
    for (const s of document.querySelectorAll<HTMLScriptElement>('script[src]:not([data-loaded])')) {
      const src = s.getAttribute('src') || '';
      if (
        src.includes('widget-loader.js') ||
        src.includes('loader.ts') ||
        /\/loader(\.[tj]s)?([?#]|$)/.test(src)
      ) {
        return s;
      }
    }
    return null;
  }

  const script = getLoaderScript();
  if (!script || script.getAttribute('data-loaded')) return;

  const clientId = (
    script.getAttribute('data-client-id') ||
    script.getAttribute('data-clientid') ||
    ''
  ).trim();

  const siteKey =
    script.getAttribute('data-site-key') ||
    script.getAttribute('data-key') ||
    clientId ||
    '';

  if (!clientId) {
    console.warn(
      '[ChatWidget] Missing data-client-id. Add data-client-id="YOUR_ID" to the script tag.\n' +
      'Get your client ID from the dashboard.'
    );
  }

  script.setAttribute('data-loaded', 'true');

  (window as Window & { __CHAT_WIDGET__?: { siteKey: string; clientId: string } }).__CHAT_WIDGET__ = {
    siteKey: siteKey || 'default',
    clientId,
  };

  const url = new URL(script.src);
  const baseUrl = url.origin + url.pathname.replace(/\/[^/]*$/, '');
  const isDev = script.src.includes('/src/loader') || script.src.includes('loader.ts');
  const widgetUrl = isDev ? `${baseUrl}/src/main.tsx` : `${baseUrl}/widget.js`;

  const loadWidget = () => {
    // Always mount as a direct child of <body> so host stacking contexts
    // (transform / filter / z-index on layout wrappers) cannot bury the widget.
    let container = document.getElementById('chat-widget-root');
    if (!container) {
      container = document.createElement('div');
      container.id = 'chat-widget-root';
    }
    container.setAttribute('data-chat-widget', 'root');
    if (container.parentElement !== document.body) {
      document.body.appendChild(container);
    }

    [
      { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
      { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossorigin: true },
      { rel: 'stylesheet', href: 'https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700&display=swap' },
      { rel: 'preconnect', href: 'https://api.fontshare.com' },
      { rel: 'stylesheet', href: 'https://api.fontshare.com/v2/css?f[]=clash-grotesk@400&f[]=clash-grotesk@500&f[]=clash-grotesk@600&f[]=clash-grotesk@700&display=swap' },
    ].forEach((f) => {
      if (f.rel === 'stylesheet' && document.querySelector(`link[href="${f.href}"]`)) return;
      const el = document.createElement('link');
      el.rel = f.rel;
      el.href = f.href;
      if (f.crossorigin) el.crossOrigin = 'anonymous';
      document.head.appendChild(el);
    });

    if (!isDev) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = baseUrl + '/widget.css';
      document.head.appendChild(link);
    }

    const s = document.createElement('script');
    s.src = widgetUrl;
    if (isDev) {
      s.type = 'module';
    }
    s.async = true;
    document.head.appendChild(s);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', loadWidget);
  } else {
    loadWidget();
  }
})();
